import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'recompose';
import styled, { keyframes } from 'styled-components';
import CreatableSelect from 'components/CreatableSelect';
import Select from 'components/FormFields/Select';
import Toggle from 'components/FormFields/Toggle';
import { ModalV2 } from 'components/Modal';
import CreateWaiver from '../gql/createWaiver';
import GetWaiver from '../gql/getWaiver';
import GetWaiverCompanies from '../gql/getWaiverCompanies';
import UpdateWaiver from '../gql/updateWaiver';

// Form styled components
const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#374151')};
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) => (props.theme.name === 'dark' ? '#374151' : '#D1D5DB')};
  border-radius: 8px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: #fa4616;
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${(props) => (props.theme.name === 'dark' ? '#6B7280' : '#9CA3AF')};
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) => (props.theme.name === 'dark' ? '#374151' : '#D1D5DB')};
  border-radius: 8px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;

  &:focus {
    border-color: #fa4616;
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${(props) => (props.theme.name === 'dark' ? '#6B7280' : '#9CA3AF')};
  }
`;

const ErrorMessage = styled.span`
  display: block;
  color: #ef4444;
  font-size: 12px;
  margin-top: 6px;
`;

const Divider = styled.div`
  height: 1px;
  background: ${(props) =>
    props.theme.name === 'dark' ? '#374151' : '#E5E7EB'};
  margin: 24px 0;
`;

const SectionTitle = styled.h3`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  margin: 0 0 16px 0;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.06)'};
  border-radius: 8px;
  margin-bottom: 12px;
`;

const ToggleInfo = styled.div`
  flex: 1;
  margin-right: 16px;
`;

const ToggleTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  margin-bottom: 4px;
`;

const ToggleDescription = styled.div`
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
`;

const SubmitButton = styled.button`
  background: #fa4616;
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #e03e0c;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: transparent;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#374151')};
  border: 1px solid
    ${(props) => (props.theme.name === 'dark' ? '#374151' : '#D1D5DB')};
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Shimmer animation for skeleton
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Skeleton base with shimmer effect
const SkeletonBase = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.06) 75%)'
      : 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)'};
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 4px;
`;

const SkeletonLabel = styled(SkeletonBase)`
  height: 14px;
  width: 100px;
  margin-bottom: 8px;
`;

const SkeletonInput = styled(SkeletonBase)`
  height: 46px;
  width: 100%;
  border-radius: 8px;
`;

const SkeletonToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.06)'};
`;

const SkeletonToggleInfo = styled.div`
  flex: 1;
`;

const SkeletonToggleTitle = styled(SkeletonBase)`
  height: 14px;
  width: 180px;
  margin-bottom: 8px;
`;

const SkeletonToggleDesc = styled(SkeletonBase)`
  height: 12px;
  width: 260px;
`;

const SkeletonToggle = styled(SkeletonBase)`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  flex-shrink: 0;
`;

const SkeletonSectionTitle = styled(SkeletonBase)`
  height: 12px;
  width: 120px;
  margin-bottom: 16px;
`;

const SkeletonTextarea = styled(SkeletonBase)`
  height: 100px;
  width: 100%;
  border-radius: 8px;
`;

const SkeletonDivider = styled.div`
  height: 1px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.08)'};
  margin: 24px 0;
`;

// Skeleton Loading Component
const FormSkeleton = () => (
  <>
    {/* Name field skeleton */}
    <div style={{ marginBottom: 20 }}>
      <SkeletonLabel />
      <SkeletonInput />
    </div>

    {/* Waiver ID field skeleton */}
    <div style={{ marginBottom: 20 }}>
      <SkeletonLabel />
      <SkeletonInput />
    </div>

    <SkeletonDivider />

    {/* Section title skeleton */}
    <SkeletonSectionTitle />

    {/* Toggle rows skeleton */}
    <SkeletonToggleRow>
      <SkeletonToggleInfo>
        <SkeletonToggleTitle />
        <SkeletonToggleDesc />
      </SkeletonToggleInfo>
      <SkeletonToggle />
    </SkeletonToggleRow>

    <SkeletonToggleRow>
      <SkeletonToggleInfo>
        <SkeletonToggleTitle />
        <SkeletonToggleDesc />
      </SkeletonToggleInfo>
      <SkeletonToggle />
    </SkeletonToggleRow>

    <SkeletonToggleRow>
      <SkeletonToggleInfo>
        <SkeletonToggleTitle />
        <SkeletonToggleDesc />
      </SkeletonToggleInfo>
      <SkeletonToggle />
    </SkeletonToggleRow>

    <SkeletonDivider />

    {/* Notes field skeleton */}
    <div>
      <SkeletonLabel />
      <SkeletonTextarea />
    </div>
  </>
);

const WaiverModal = ({
  isOpen,
  onClose,
  currentWaiver,
  data,
  companiesData,
  createWaiver,
  updateWaiver,
  onSuccess,
  onError,
}) => {
  const isEditMode = !!currentWaiver;
  const waiver = data?.getWaiver;
  const waiverCompanies = companiesData?.getWaiverCompanies || [];
  const isLoading = isEditMode && data?.loading;
  const companiesLoading = companiesData?.loading;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    waiver_id: '',
    type: 'waiver',
    insurance_company: null,
    status: 'active',
    requires_spouse: false,
    requires_minor: false,
    is_minor_waiver: false,
    notes: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasRefetchedCompanies = useRef(false);

  // Refetch companies when modal opens (only once per open)
  useEffect(() => {
    if (isOpen && !hasRefetchedCompanies.current && companiesData?.refetch) {
      hasRefetchedCompanies.current = true;
      companiesData.refetch();
    }
    if (!isOpen) {
      hasRefetchedCompanies.current = false;
    }
  }, [isOpen, companiesData]);

  // Reset form when modal opens/closes or waiver changes
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && waiver) {
        // Normalize status to lowercase and ensure valid value
        const normalizedStatus =
          waiver.status?.toLowerCase() === 'inactive' ? 'inactive' : 'active';
        // Normalize type to lowercase and ensure valid value
        const normalizedType =
          waiver.type?.toLowerCase() === 'agreement' ? 'agreement' : 'waiver';
        setFormData({
          name: waiver.name || '',
          waiver_id: waiver.waiver_id || '',
          type: normalizedType,
          insurance_company: waiver.company
            ? {
                value: waiver.company,
                label: waiver.company,
              }
            : null,
          status: normalizedStatus,
          requires_spouse: waiver.requires_spouse || false,
          requires_minor: waiver.requires_minor || false,
          is_minor_waiver: waiver.is_minor_waiver || false,
          notes: waiver.notes || '',
        });
      } else {
        setFormData({
          name: '',
          waiver_id: '',
          type: 'waiver',
          insurance_company: null,
          status: 'active',
          requires_spouse: false,
          requires_minor: false,
          is_minor_waiver: false,
          notes: '',
        });
      }
      setErrors({});
    }
  }, [isOpen, isEditMode, waiver]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleToggleChange = (name) => (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleInsuranceCompanyChange = (option) => {
    setFormData((prev) => ({ ...prev, insurance_company: option }));
    if (errors.insurance_company) {
      setErrors((prev) => ({ ...prev, insurance_company: null }));
    }
  };

  const handleCreateInsuranceCompany = (name) => {
    // Return the new company name - it will be sent to the BE as part of the waiver payload
    // The BE will create the company if it doesn't exist
    return {
      value: name, // Use name as value for new companies
      label: name,
      isNew: true, // Flag to indicate this is a new company name
    };
  };

  // Transform companies for select options - waiverCompanies is an array of strings
  const companyOptions = waiverCompanies.map((company) => ({
    value: company,
    label: company,
  }));

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Waiver name is required';
    }

    if (!formData.waiver_id.trim()) {
      newErrors.waiver_id = 'Waiver ID is required';
    }

    if (!formData.type) {
      newErrors.type = 'Type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim() || null,
        waiver_id: formData.waiver_id.trim() || null,
        type: formData.type,
        company: formData.insurance_company?.label || null,
        status: formData.status,
        requires_spouse: formData.requires_spouse,
        requires_minor: formData.requires_minor,
        is_minor_waiver: formData.is_minor_waiver,
        notes: formData.notes.trim() || null,
      };

      let response;

      if (isEditMode) {
        response = await updateWaiver({ ...payload, id: currentWaiver });
      } else {
        response = await createWaiver(payload);
      }

      if (!response || response.errors) {
        const errorMsg = isEditMode
          ? 'Error updating waiver'
          : 'Error creating waiver';
        if (onError) onError(errorMsg);

        if (response?.errors) {
          setErrors(response.errors);
        }
      } else {
        const successMsg = isEditMode
          ? 'Waiver updated successfully'
          : 'Waiver created successfully';
        if (onSuccess) onSuccess(successMsg);
        onClose();
      }
    } catch (error) {
      if (onError) onError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <ModalV2
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Edit Waiver' : 'Create Waiver'}
      subtitle={
        isEditMode
          ? 'Update the waiver details below'
          : 'Fill in the details to create a new waiver'
      }
      maxWidth="560px"
      footer={
        <>
          <CancelButton
            onClick={handleClose}
            disabled={isSubmitting || isLoading}
          >
            Cancel
          </CancelButton>
          <SubmitButton
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting
              ? 'Saving...'
              : isEditMode
              ? 'Update Waiver'
              : 'Create Waiver'}
          </SubmitButton>
        </>
      }
    >
      {isLoading ? (
        <FormSkeleton />
      ) : (
        <>
          {/* Basic Information */}
          <FormGroup>
            <FormLabel htmlFor="name">Waiver Name *</FormLabel>
            <FormInput
              id="name"
              name="name"
              type="text"
              placeholder="Enter waiver name"
              value={formData.name}
              onChange={handleInputChange}
              disabled={isSubmitting}
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="waiver_id">Waiver ID *</FormLabel>
            <FormInput
              id="waiver_id"
              name="waiver_id"
              type="text"
              placeholder="Enter waiver ID"
              value={formData.waiver_id}
              onChange={handleInputChange}
              disabled={isSubmitting}
            />
            {errors.waiver_id && (
              <ErrorMessage>{errors.waiver_id}</ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Select
              label="Type *"
              value={formData.type || 'waiver'}
              onChange={(e) => {
                const newType = e.target.value;
                setFormData((prev) => ({ ...prev, type: newType }));
                if (errors.type) {
                  setErrors((prev) => ({ ...prev, type: null }));
                }
              }}
              options={[
                { label: 'Waiver', value: 'waiver' },
                { label: 'Agreement', value: 'agreement' },
              ]}
              disabled={isSubmitting}
            />
            {errors.type && <ErrorMessage>{errors.type}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <CreatableSelect
              label="Insurance Company"
              options={companyOptions}
              value={formData.insurance_company}
              onChange={handleInsuranceCompanyChange}
              onCreateOption={handleCreateInsuranceCompany}
              placeholder="Select or create insurance company..."
              loading={companiesLoading}
              disabled={isSubmitting}
              createLabel="Add new company"
              error={errors.insurance_company}
            />
          </FormGroup>

          <FormGroup>
            <Select
              label="Status"
              value={formData.status || 'active'}
              onChange={(e) => {
                const newStatus = e.target.value;
                setFormData((prev) => ({ ...prev, status: newStatus }));
              }}
              options={[
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' },
              ]}
              disabled={isSubmitting}
            />
          </FormGroup>

          <Divider />

          {/* Waiver Options */}
          <SectionTitle>Waiver Options</SectionTitle>

          <ToggleRow>
            <ToggleInfo>
              <ToggleTitle>Requires Second Parent Signature</ToggleTitle>
              <ToggleDescription>
                Enable if this waiver requires a spouse or second parent to sign
              </ToggleDescription>
            </ToggleInfo>
            <Toggle
              checked={formData.requires_spouse}
              onChange={handleToggleChange('requires_spouse')}
              disabled={isSubmitting}
            />
          </ToggleRow>

          <ToggleRow>
            <ToggleInfo>
              <ToggleTitle>Requires Minor Signature</ToggleTitle>
              <ToggleDescription>
                Enable if minors are required to sign this waiver
              </ToggleDescription>
            </ToggleInfo>
            <Toggle
              checked={formData.requires_minor}
              onChange={handleToggleChange('requires_minor')}
              disabled={isSubmitting}
            />
          </ToggleRow>

          <ToggleRow>
            <ToggleInfo>
              <ToggleTitle>Is Minor Waiver</ToggleTitle>
              <ToggleDescription>
                Enable if this waiver is specifically for minors
              </ToggleDescription>
            </ToggleInfo>
            <Toggle
              checked={formData.is_minor_waiver}
              onChange={handleToggleChange('is_minor_waiver')}
              disabled={isSubmitting}
            />
          </ToggleRow>

          <Divider />

          {/* Notes */}
          <FormGroup>
            <FormLabel htmlFor="notes">Notes</FormLabel>
            <FormTextarea
              id="notes"
              name="notes"
              placeholder="Add any additional notes about this waiver..."
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isSubmitting}
              rows={4}
            />
          </FormGroup>
        </>
      )}
    </ModalV2>
  );
};

WaiverModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentWaiver: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.object,
  companiesData: PropTypes.object,
  createWaiver: PropTypes.func.isRequired,
  updateWaiver: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};

export default compose(
  GetWaiver,
  GetWaiverCompanies,
  CreateWaiver,
  UpdateWaiver
)(WaiverModal);
